import React, { useRef, useState } from 'react';
import { MoveOutCharges } from '../../../utils/portvilleMoveOutCharges';
import SmsService from '../../../services/SmsService';

interface MoveOutChargesSummaryProps {
  charges: MoveOutCharges;
  recipient?: { name?: string; phoneNumber?: string };
  disabled?: boolean;
}

const MoveOutChargesSummary: React.FC<MoveOutChargesSummaryProps> = ({ charges, recipient, disabled = false }) => {
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [feedback, setFeedback] = useState<{ key: string; error: boolean; message: string } | null>(null);
  const depositAccount = charges.depositAccount?.split(/\r?\n/).map(line => line.trim()).join('\n').trim() || '';
  const phoneNumber = (recipient?.phoneNumber || '').replace(/[\s()-]/g, '');
  const validPhone = /^0\d{8,10}$/.test(phoneNumber);
  const message = `[포트빌 퇴거점검 입금 안내]\n${depositAccount}\n입금 총액 : ${charges.total.toLocaleString('ko-KR')}원`;
  const feedbackKey = JSON.stringify([phoneNumber, message]);
  const unavailableReason = !validPhone ? '입주자의 유효한 연락처가 필요합니다.'
    : !depositAccount ? '입금계좌가 등록되어야 SMS를 발송할 수 있습니다.' : '';

  const sendPaymentSms = async () => {
    if (disabled || sendingRef.current || unavailableReason || !recipient) return;
    sendingRef.current = true;
    setSending(true);
    setFeedback(null);
    try {
      await SmsService.sendSMS(phoneNumber, message, { subject: '포트빌 퇴거점검 입금 안내' });
      setFeedback({ key: feedbackKey, error: false, message: 'SMS 발송을 요청했습니다.' });
    } catch (error) {
      setFeedback({ key: feedbackKey, error: true,
        message: error instanceof Error ? error.message : 'SMS 발송에 실패했습니다. 다시 시도해주세요.' });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const rows = [
    ['관리비', charges.management], ['전기요금', charges.electricity],
    ['수도요금', charges.water], ['통신요금', charges.communication]
  ] as const;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        점검일 {charges.inspectionDate} (한국시간) · 관리비: {charges.daysInMonth}일인 달의 1일~{charges.chargeDays}일, 당일 포함 금액표 적용
      </p>
      <p className="text-sm text-gray-600">통신 사용: {charges.communicationUsed ? '유' : '무'}</p>
      <p className="text-sm text-gray-600">전월 관리비: {charges.includePreviousMonth ? '포함' : '미포함'}</p>
      {charges.includePreviousMonth && (
        <p className="text-sm text-gray-600">
          관리비는 일할계산 금액에 전월 공급가액 86,500원과 부가세 8,650원을 합산하며, 전기·수도·통신요금은 2개월분을 적용합니다. 통신 미사용 시 통신요금은 0원입니다.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-right whitespace-nowrap">
          <caption className="sr-only">퇴거점검 납부 금액 (원)</caption>
          <thead className="bg-gray-50"><tr>
            <th scope="col" className="p-2 text-left">항목</th><th scope="col" className="p-2">공급가액</th>
            <th scope="col" className="p-2">부가세</th><th scope="col" className="p-2">합계</th>
          </tr></thead>
          <tbody>{rows.map(([name, charge]) => (
            <tr key={name} className="border-b border-gray-100">
              <th scope="row" className="p-2 text-left font-medium">{name}</th>
              <td className="p-2">{charge.supplyAmount.toLocaleString('ko-KR')}원</td>
              <td className="p-2">{charge.vat.toLocaleString('ko-KR')}원</td>
              <td className="p-2 font-medium">{charge.total.toLocaleString('ko-KR')}원</td>
            </tr>
          ))}</tbody>
          <tfoot><tr className="bg-blue-50 font-semibold">
            <th scope="row" colSpan={3} className="p-2 text-left">총 납부금액</th>
            <td className="p-2">{charges.total.toLocaleString('ko-KR')}원</td>
          </tr></tfoot>
        </table>
      </div>
      <div className="rounded-md bg-gray-50 p-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-semibold text-gray-900">입금계좌</div>
          {recipient && (
            <button type="button" onClick={sendPaymentSms}
              disabled={disabled || sending || Boolean(unavailableReason)}
              className="rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
              {sending ? 'SMS 발송 중...' : 'SMS 발송'}
            </button>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap text-gray-700">{depositAccount || '입금계좌 미등록'}</p>
        {recipient && (
          <div className="mt-2 space-y-1">
            <p className="text-gray-600">수신자: {recipient.name || '입주자'} ({recipient.phoneNumber || '연락처 미등록'})</p>
            {unavailableReason && <p className="text-red-700">{unavailableReason}</p>}
            {feedback?.key === feedbackKey && (
              <p role={feedback.error ? 'alert' : 'status'} className={feedback.error ? 'text-red-700' : 'text-blue-700'}>
                {feedback.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MoveOutChargesSummary;
